import { type CSSProperties, useEffect, useMemo, useState } from "react";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { Badge, Col, Row, Spinner } from "reactstrap";
import DatePicker, { DateObject } from "react-multi-date-picker";

import { Btn, P } from "../../../../AbstractElements";
import IconComponent from "../../../../CommonElements/IconComponent";
import { IUserMasterDetails } from "../../../../Api/Services/HRService";
import {
  useAssignMenuToUserMutation,
  useLazyAssignMenusUserQuery,
} from "../../../../ReduxToolkit/api/menus";
import { showApiResultAlert } from "../../../../utils/AlertManager/showApiAlert";
import { IMenusList } from "../../types";
import "./UserMenuForm.scss";

interface IUserMenuFormProps {
  menus: IMenusList[];
  users: IUserMasterDetails[];
  userTotalCount?: number;
  userFilters: IUserMenuUserFilters;
  userFilterOptions: IUserMenuFilterOptions;
  filtersLoading?: boolean;
  usersLoading?: boolean;
  onUserFiltersChange: (filters: Partial<IUserMenuUserFilters>) => void;
}

interface IUserMenuFormValues {
  users: IUserOption[];
  menuIds: number[];
}

export interface IUserMenuUserFilters {
  search: string;
  status: string;
  jobType: string;
  departmentId: string;
  centerId: string;
  dateFilter: string;
  dateRange: DateObject[];
}

export interface IUserMenuFilterOptions {
  jobTypeOptions: Array<{ label: string; value: string | number }>;
  departmentOptions: Array<{ label: string; value: string | number }>;
  centerOptions: Array<{ label: string; value: string | number }>;
}

type IUserOption = IUserMasterDetails & {
  id: number;
  name: string;
  email: string;
};

interface IMenuNodeProps {
  menu: IMenusList;
  level?: number;
  selectedIds: number[];
  expandedIds: number[];
  onToggleExpand: (menuId: number) => void;
  onToggleMenu: (menu: IMenusList) => void;
}

type MenuChild = IMenusList | { data: IMenusList[] };
type MenuRowFilter = "all" | "parentMenus" | "parentRoutes" | "childRoutes";

const menuRowFilterOptions: Array<{ label: string; value: MenuRowFilter }> = [
  { label: "All Menus", value: "all" },
  { label: "Parent Menus", value: "parentMenus" },
  { label: "Parent Routes", value: "parentRoutes" },
  { label: "Child Routes", value: "childRoutes" },
];

const extractAssignedMenuIds = (response: unknown): number[] => {
  const payload = response as any;
  const directAssignedIds = payload?.data;

  if (
    Array.isArray(directAssignedIds) &&
    directAssignedIds.every(
      (item) => typeof item === "number" || typeof item === "string",
    )
  ) {
    return Array.from(new Set(directAssignedIds.map(Number).filter(Boolean)));
  }

  return [];
};

const validationSchema = Yup.object().shape({
  users: Yup.array().min(1, "Select at least one user"),
  menuIds: Yup.array().min(1, "Select at least one menu"),
});

const statusOptions = [
  { label: "All", value: "" },
  { label: "Active", value: "1" },
  { label: "Inactive", value: "2" },
];

const dateFilterOptions = [
  { label: "Date Filter", value: "Select" },
  { label: "Date Of Joining", value: "doj" },
  { label: "Date Of Birth", value: "dob" },
  { label: "Anniversary", value: "anniversary" },
  { label: "Exit", value: "exit" },
];

const getOptionLabel = (
  options: Array<{ label: string; value: string | number }>,
  value: string,
) => options.find((option) => String(option.value) === String(value))?.label;

const getChildren = (menu: IMenusList): IMenusList[] => {
  const children = menu.children as MenuChild[] | undefined;

  if (!Array.isArray(children)) return [];

  return children.flatMap((child) => {
    if ("data" in child && Array.isArray(child.data)) {
      return child.data;
    }

    return child as IMenusList;
  });
};

const getMenuIds = (menu: IMenusList): number[] => [
  menu.id,
  ...getChildren(menu).flatMap(getMenuIds),
];

const hasMenuRoute = (menu: IMenusList) =>
  Boolean(String(menu.route ?? "").trim());

const matchesMenuRowFilter = (
  menu: IMenusList,
  filter: MenuRowFilter,
  hasParent: boolean,
) => {
  if (filter === "parentMenus") return !hasMenuRoute(menu) && !hasParent;
  if (filter === "parentRoutes") return hasMenuRoute(menu) && !hasParent;
  if (filter === "childRoutes") return hasMenuRoute(menu) && hasParent;

  return true;
};

const filterMenuHierarchyByRowFilter = (
  menus: IMenusList[],
  filter: MenuRowFilter,
  hasAncestor = false,
): IMenusList[] => {
  if (filter === "all") return menus;

  return menus.reduce<IMenusList[]>((filteredMenus, menu) => {
    const menuHasParent = hasAncestor || Boolean(menu.parent_id);
    const children = getChildren(menu);
    const filteredChildren = filterMenuHierarchyByRowFilter(
      children,
      filter,
      true,
    );
    const matchesFilter = matchesMenuRowFilter(menu, filter, menuHasParent);

    if (matchesFilter) {
      filteredMenus.push(menu);
      return filteredMenus;
    }

    if (filteredChildren.length) {
      filteredMenus.push({ ...menu, children: filteredChildren });
    }

    return filteredMenus;
  }, []);
};

const getSelectableMenuIds = (menu: IMenusList): number[] => {
  const currentMenuId = Number(menu.id);
  const currentMenuIds =
    Number(menu.is_default) === 1 || !currentMenuId ? [] : [currentMenuId];

  return [
    ...currentMenuIds,
    ...getChildren(menu).flatMap(getSelectableMenuIds),
  ];
};

const getAllMenuIds = (menus: IMenusList[]): number[] =>
  menus.flatMap((menu) => getMenuIds(menu));

const getMenuById = (
  menus: IMenusList[],
  targetMenuId: number,
): IMenusList | null => {
  for (const menu of menus) {
    if (Number(menu.id) === targetMenuId) return menu;

    const childMenu = getMenuById(getChildren(menu), targetMenuId);

    if (childMenu) return childMenu;
  }

  return null;
};

const getMenuPath = (
  menus: IMenusList[],
  targetMenuId: number,
  path: IMenusList[] = [],
): IMenusList[] => {
  for (const menu of menus) {
    const nextPath = [...path, menu];

    if (Number(menu.id) === targetMenuId) return nextPath;

    const childPath = getMenuPath(getChildren(menu), targetMenuId, nextPath);

    if (childPath.length) return childPath;
  }

  return [];
};

const getDescendantMenuIds = (menu: IMenusList): number[] =>
  getChildren(menu).flatMap(getMenuIds);

const getMenuActionIds = (
  action: "attach" | "detach",
  menu: IMenusList,
  assignedMenuIds: number[],
  menuHierarchy: IMenusList[],
) => {
  const assignedSet = new Set(assignedMenuIds);
  const menuId = Number(menu.id);
  const menuPath = getMenuPath(menuHierarchy, menuId);
  const ancestorIds = menuPath.slice(0, -1).map((item) => Number(item.id));

  if (action === "attach") {
    return Array.from(new Set([...ancestorIds, menuId]));
  }

  const nextAssignedSet = new Set(assignedSet);
  nextAssignedSet.delete(menuId);

  const parentIdsToDetach = menuPath
    .slice(0, -1)
    .reverse()
    .filter((parentMenu) => Number(parentMenu.is_default) !== 1)
    .filter((parentMenu) => {
      const hasAssignedChild = getDescendantMenuIds(parentMenu).some((id) =>
        nextAssignedSet.has(id),
      );

      if (!hasAssignedChild) {
        nextAssignedSet.delete(Number(parentMenu.id));
        return true;
      }

      return false;
    })
    .map((parentMenu) => Number(parentMenu.id));

  return Array.from(new Set([menuId, ...parentIdsToDetach]));
};

const buildMenuHierarchy = (menus: IMenusList[]) => {
  const menuMap = new Map<number, IMenusList>();
  const explicitParentMap = new Map<number, number>();

  const collect = (menuList: IMenusList[], parentId?: number) => {
    menuList.forEach((menu) => {
      if (!menuMap.has(menu.id)) {
        menuMap.set(menu.id, { ...menu, children: [] });
      }

      if (parentId && !explicitParentMap.has(menu.id)) {
        explicitParentMap.set(menu.id, parentId);
      }

      collect(getChildren(menu), menu.id);
    });
  };

  collect(menus);

  const roots: IMenusList[] = [];

  menuMap.forEach((menu) => {
    const parentId = menu.parent_id ?? explicitParentMap.get(menu.id);
    const parentMenu = parentId ? menuMap.get(parentId) : null;

    if (parentMenu) {
      parentMenu.children = [...getChildren(parentMenu), menu];
      return;
    }

    roots.push(menu);
  });

  return roots;
};

const filterMenuHierarchy = (
  menus: IMenusList[],
  query: string,
): IMenusList[] => {
  const normalizedQuery = query.trim().toLowerCase();

  if (!normalizedQuery) return menus;

  return menus.reduce<IMenusList[]>((filteredMenus, menu) => {
    const children = getChildren(menu);
    const filteredChildren = filterMenuHierarchy(children, normalizedQuery);
    const matchesMenu = [menu.name, menu.route, menu.icon]
      .filter(Boolean)
      .some((value) => String(value).toLowerCase().includes(normalizedQuery));

    if (matchesMenu) {
      filteredMenus.push(menu);
      return filteredMenus;
    }

    if (filteredChildren.length) {
      filteredMenus.push({ ...menu, children: filteredChildren });
    }

    return filteredMenus;
  }, []);
};

const getExpandableIds = (menus: IMenusList[]): number[] =>
  menus.flatMap((menu) => {
    const children = getChildren(menu);

    return children.length ? [menu.id, ...getExpandableIds(children)] : [];
  });

const normalizeUsers = (users: IUserMasterDetails[]): IUserOption[] =>
  users
    .filter((user): user is IUserMasterDetails & { id: number } =>
      Boolean(user.id),
    )
    .map((user) => ({
      ...user,
      id: user.id,
      name:
        user.employee_name ||
        user.official_email ||
        user.employee_code ||
        `User ${user.id}`,
      email: user.official_email || "",
    }));

const MenuNode = ({
  menu,
  level = 0,
  selectedIds,
  expandedIds,
  onToggleExpand,
  onToggleMenu,
}: IMenuNodeProps) => {
  const children = getChildren(menu);
  const isExpanded = expandedIds.includes(menu.id);
  const isParent = children.length > 0;
  const isDefaultMenu = Number(menu.is_default) === 1;
  const selectableIds = getSelectableMenuIds(menu);
  const selectedCount = selectableIds.filter((id) =>
    selectedIds.includes(id),
  ).length;
  const isSelected =
    selectableIds.length > 0 && selectedCount === selectableIds.length;
  const isPartial = selectedCount > 0 && !isSelected;
  const isDisabled = isDefaultMenu || !selectableIds.length;

  return (
    <>
      <div
        className={`permission-row ${isParent ? "is-parent" : "is-child"} ${
          isSelected ? "is-selected" : ""
        } ${isPartial ? "is-partial" : ""} ${
          isDefaultMenu ? "is-default" : ""
        }`}
        style={{ "--level": level } as CSSProperties}
      >
        <div className="permission-row__main">
          <input
            className="form-check-input permission-row__check"
            type="checkbox"
            checked={isSelected}
            disabled={isDisabled}
            ref={(input) => {
              if (input) input.indeterminate = isPartial;
            }}
            onChange={() => onToggleMenu(menu)}
          />
          {isParent ? (
            <button
              type="button"
              className="permission-row__toggle"
              onClick={() => onToggleExpand(menu.id)}
              aria-label={
                isExpanded ? "Collapse route group" : "Expand route group"
              }
            >
              <IconComponent
                className={`fa ${
                  isExpanded ? "fa-chevron-down" : "fa-chevron-right"
                }`}
              />
            </button>
          ) : (
            <span className="permission-row__toggle-placeholder" />
          )}
          <span className="permission-row__icon">
            <IconComponent
              className={`fa ${isParent ? "fa-folder-o" : "fa-link"}`}
            />
          </span>
          <div className="permission-row__copy">
            <P className="permission-row__name">{menu.name}</P>
          </div>
        </div>
        <div className="permission-row__meta">
          {isParent ? (
            <Badge className="permission-row__count" color="light" pill>
              {getChildren(menu).length}
            </Badge>
          ) : isDefaultMenu ? (
            <span className="permission-row__default">Default</span>
          ) : null}
        </div>
      </div>
      {isParent && isExpanded && (
        <div className="permission-children">
          {children.map((child) => (
            <MenuNode
              key={child.id}
              menu={child}
              level={level + 1}
              selectedIds={selectedIds}
              expandedIds={expandedIds}
              onToggleExpand={onToggleExpand}
              onToggleMenu={onToggleMenu}
            />
          ))}
        </div>
      )}
    </>
  );
};

const UserMenuForm = ({
  menus,
  users,
  userTotalCount,
  userFilters,
  userFilterOptions,
  filtersLoading = false,
  usersLoading = false,
  onUserFiltersChange,
}: IUserMenuFormProps) => {
  const [expandedIds, setExpandedIds] = useState<number[]>([]);
  const [filtersCollapsed, setFiltersCollapsed] = useState(true);
  const [routeSearch, setRouteSearch] = useState("");
  const [menuRowFilter, setMenuRowFilter] = useState<MenuRowFilter>("all");
  const [accessAction, setAccessAction] = useState<"attach" | "detach" | null>(
    null,
  );
  const [assignMenuToUser] = useAssignMenuToUserMutation();
  const [fetchAssignedMenus] = useLazyAssignMenusUserQuery();
  const userOptions = useMemo(() => normalizeUsers(users), [users]);
  const routeGroups = useMemo(() => buildMenuHierarchy(menus), [menus]);
  const filteredRouteGroups = useMemo(
    () => filterMenuHierarchyByRowFilter(routeGroups, menuRowFilter),
    [menuRowFilter, routeGroups],
  );
  const visibleRouteGroups = useMemo(
    () => filterMenuHierarchy(filteredRouteGroups, routeSearch),
    [filteredRouteGroups, routeSearch],
  );
  const expandableIds = useMemo(
    () => getExpandableIds(routeGroups),
    [routeGroups],
  );
  const visibleMenuIds = useMemo(
    () => Array.from(new Set(getAllMenuIds(visibleRouteGroups))),
    [visibleRouteGroups],
  );

  useEffect(() => {
    setExpandedIds(expandableIds);
  }, [expandableIds]);

  const toggleExpand = (menuId: number) => {
    setExpandedIds((currentIds) =>
      currentIds.includes(menuId)
        ? currentIds.filter((id) => id !== menuId)
        : [...currentIds, menuId],
    );
  };

  const updateAdvancedFilter = (
    filters: Partial<IUserMenuUserFilters>,
    shouldCollapse = true,
  ) => {
    onUserFiltersChange(filters);
    if (shouldCollapse) setFiltersCollapsed(true);
  };

  const handleMenuAccessAction = async (
    action: "attach" | "detach",
    selectedUsers: IUserOption[],
    selectedMenuIds: number[],
  ) => {
    const uniqueUsers = Array.from(
      new Map(selectedUsers.map((user) => [user.id, user])).values(),
    );
    const uniqueMenuIds = Array.from(new Set(selectedMenuIds.map(Number)))
      .map((menuId) => ({
        menuId,
        menu: getMenuById(routeGroups, menuId),
        depth: getMenuPath(routeGroups, menuId).length,
      }))
      .filter(
        (
          item,
        ): item is {
          menuId: number;
          menu: IMenusList;
          depth: number;
        } => Boolean(item.menu) && Number(item.menu?.is_default) !== 1,
      )
      .sort((firstItem, secondItem) =>
        action === "detach"
          ? secondItem.depth - firstItem.depth
          : firstItem.depth - secondItem.depth,
      );

    if (!uniqueUsers.length) {
      showApiResultAlert(
        {
          data: {
            status: false,
            message: "Select at least one user before updating menu access.",
          },
        },
        {
          title: "Assign User Menu",
          source: "assignUserMenu",
        },
      );
      return false;
    }

    if (!uniqueMenuIds.length) {
      showApiResultAlert(
        {
          data: {
            status: false,
            message: "Select at least one non-default menu before updating.",
          },
        },
        {
          title: "Assign User Menu",
          source: "assignUserMenu",
        },
      );
      return false;
    }

    setAccessAction(action);

    try {
      if (action === "attach") {
        const menuIdSet = new Set<number>();

        uniqueMenuIds.forEach(({ menu }) => {
          getMenuActionIds("attach", menu, [], routeGroups)
            .filter((id) => {
              const actionMenu = getMenuById(routeGroups, id);

              return Boolean(id) && Number(actionMenu?.is_default) !== 1;
            })
            .forEach((id) => menuIdSet.add(id));
        });

        const menuIds = Array.from(menuIdSet).sort(
          (firstId, secondId) => firstId - secondId,
        );
        const response = await assignMenuToUser({
          user_ids: uniqueUsers.map((user) => user.id),
          menu_ids: menuIds,
          action,
        });

        showApiResultAlert(response, {
          title: "Assign User Menu",
          source: "assignUserMenu",
        });

        return "data" in response;
      }

      const groupedPayloads = new Map<
        string,
        { userIds: number[]; menuIds: number[] }
      >();

      for (const selectedUser of uniqueUsers) {
        const assignedResponse = await fetchAssignedMenus(
          selectedUser.id,
        ).unwrap();
        const assignedMenuIds = extractAssignedMenuIds(assignedResponse);
        const assignedSet = new Set(assignedMenuIds);
        const menuIdSet = new Set<number>();

        uniqueMenuIds.forEach(({ menuId, menu }) => {
          const hasMenuAssigned = assignedSet.has(menuId);

          if (!hasMenuAssigned) return;

          getMenuActionIds(action, menu, Array.from(assignedSet), routeGroups)
            .filter((id) => {
              const actionMenu = getMenuById(routeGroups, id);

              return Boolean(id) && Number(actionMenu?.is_default) !== 1;
            })
            .forEach((id) => {
              menuIdSet.add(id);
              assignedSet.delete(id);
            });
        });

        const menuIds = Array.from(menuIdSet).sort(
          (firstId, secondId) => firstId - secondId,
        );

        if (!menuIds.length) continue;

        const payloadKey = menuIds.join(",");
        const existingPayload = groupedPayloads.get(payloadKey);

        if (existingPayload) {
          existingPayload.userIds.push(selectedUser.id);
        } else {
          groupedPayloads.set(payloadKey, {
            userIds: [selectedUser.id],
            menuIds,
          });
        }
      }

      if (!groupedPayloads.size) {
        showApiResultAlert(
          {
            data: {
              status: false,
              message:
                "Selected users do not have the selected menus assigned.",
            },
          },
          {
            title: "Assign User Menu",
            source: "assignUserMenu",
          },
        );
        return false;
      }

      let lastResponse: Awaited<ReturnType<typeof assignMenuToUser>> | null =
        null;

      for (const payload of Array.from(groupedPayloads.values())) {
        lastResponse = await assignMenuToUser({
          user_ids: payload.userIds,
          menu_ids: payload.menuIds,
          action,
        });
      }

      if (lastResponse) {
        showApiResultAlert(lastResponse, {
          title: "Assign User Menu",
          source: "assignUserMenu",
        });

        return "data" in lastResponse;
      }
    } catch {
      showApiResultAlert(
        {
          error: { message: "Unable to update menu access." },
        },
        {
          title: "Assign User Menu",
          source: "assignUserMenu",
        },
      );
    } finally {
      setAccessAction(null);
    }

    return false;
  };

  const dateFilterLabel = dateFilterOptions.find(
    (option) => option.value === userFilters.dateFilter,
  )?.label;
  const activeFilterLabels = [
    userFilters.status !== "1"
      ? (statusOptions.find((option) => option.value === userFilters.status)
          ?.label ?? "All Status")
      : "",
    userFilters.jobType
      ? getOptionLabel(userFilterOptions.jobTypeOptions, userFilters.jobType)
      : "",
    userFilters.departmentId
      ? getOptionLabel(
          userFilterOptions.departmentOptions,
          userFilters.departmentId,
        )
      : "",
    userFilters.centerId
      ? getOptionLabel(userFilterOptions.centerOptions, userFilters.centerId)
      : "",
    userFilters.dateFilter !== "Select" ? dateFilterLabel : "",
  ].filter(Boolean);

  return (
    <Formik<IUserMenuFormValues>
      initialValues={{ users: [], menuIds: [] }}
      validationSchema={validationSchema}
      onSubmit={() => undefined}
    >
      {({ errors, setFieldValue, touched, values }) => {
        const selectedUserIds = new Set(values.users.map((user) => user.id));
        const orderedUserOptions = [...userOptions].sort(
          (firstUser, secondUser) =>
            Number(selectedUserIds.has(secondUser.id)) -
            Number(selectedUserIds.has(firstUser.id)),
        );
        const selectedFilteredUserCount = userOptions.filter((user) =>
          selectedUserIds.has(user.id),
        ).length;
        const allFilteredUsersSelected =
          userOptions.length > 0 &&
          selectedFilteredUserCount === userOptions.length;

        const selectFilteredUsers = () => {
          const userMap = new Map<number, IUserOption>();

          values.users.forEach((user) => userMap.set(user.id, user));
          userOptions.forEach((user) => userMap.set(user.id, user));

          setFieldValue("users", Array.from(userMap.values()));
        };

        const toggleMenu = (menu: IMenusList) => {
          const menuIds = getSelectableMenuIds(menu);

          if (!menuIds.length || Number(menu.is_default) === 1) return;

          const selectedMenuIdSet = new Set(values.menuIds);
          const allMenuIdsSelected = menuIds.every((id) =>
            selectedMenuIdSet.has(id),
          );

          menuIds.forEach((id) => {
            if (allMenuIdsSelected) {
              selectedMenuIdSet.delete(id);
            } else {
              selectedMenuIdSet.add(id);
            }
          });

          setFieldValue("menuIds", Array.from(selectedMenuIdSet));
        };

        const clearSelectedUsers = () => setFieldValue("users", []);

        const toggleUser = (user: IUserOption) => {
          const userMap = new Map<number, IUserOption>();

          values.users.forEach((selectedUser) =>
            userMap.set(selectedUser.id, selectedUser),
          );

          if (userMap.has(user.id)) {
            userMap.delete(user.id);
          } else {
            userMap.set(user.id, user);
          }

          setFieldValue("users", Array.from(userMap.values()));
        };

        const clearUserFilters = () => {
          onUserFiltersChange({
            search: "",
            status: "1",
            jobType: "",
            departmentId: "",
            centerId: "",
            dateFilter: "Select",
            dateRange: [],
          });
          setFiltersCollapsed(false);
        };

        const hasActiveUserFilter =
          userFilters.search ||
          userFilters.status !== "1" ||
          userFilters.jobType ||
          userFilters.departmentId ||
          userFilters.centerId ||
          userFilters.dateFilter !== "Select" ||
          userFilters.dateRange.length > 0;

        return (
          <Form className="theme-form user-menu-access-form">
            <Row className="g-3">
              <Col xl={4} lg={5}>
                <section className="access-pane access-pane--users">
                  <div className="access-pane__header">
                    <div>
                      <P className="access-pane__title">Users</P>
                      <P className="access-pane__subtitle">
                        Active employee report list
                      </P>
                    </div>
                    <div className="user-count-stack">
                      <Badge
                        className="selected-count-badge"
                        color="primary"
                        pill
                      >
                        {values.users.length} selected
                      </Badge>
                      <span>{userTotalCount ?? userOptions.length} found</span>
                    </div>
                  </div>
                  <div className="access-pane__content">
                    <div
                      className={`employee-report-filters ${
                        filtersCollapsed ? "is-collapsed" : ""
                      }`}
                    >
                      <div className="employee-filter-toolbar">
                        <div className="employee-report-search">
                          <IconComponent className="fa fa-search" />
                          <input
                            type="search"
                            className="form-control"
                            placeholder="Search by user name"
                            value={userFilters.search}
                            onChange={(event) =>
                              onUserFiltersChange({
                                search: event.target.value,
                              })
                            }
                          />
                        </div>
                        <button
                          type="button"
                          className="filter-toggle-btn"
                          onClick={() =>
                            setFiltersCollapsed((isCollapsed) => !isCollapsed)
                          }
                        >
                            <IconComponent
                            className={`fa ${
                              filtersCollapsed
                                ? "fa-sliders"
                                : "fa-chevron-up"
                            }`}
                          />
                          {filtersCollapsed ? "Show Filters" : "Hide Filters"}
                        </button>
                      </div>
                      {!!activeFilterLabels.length && (
                        <div className="active-filter-chips">
                          {activeFilterLabels.map((label) => (
                            <span key={label}>{label}</span>
                          ))}
                        </div>
                      )}
                      <div
                        className={`filter-grid ${
                          filtersCollapsed ? "is-collapsed" : ""
                        }`}
                      >
                        {!filtersCollapsed && (
                          <>
                            <select
                              className="form-select"
                              value={userFilters.status}
                              onChange={(event) =>
                                updateAdvancedFilter({
                                  status: event.target.value,
                                })
                              }
                            >
                              {statusOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                            <select
                              className="form-select"
                              value={userFilters.jobType}
                              disabled={filtersLoading}
                              onChange={(event) =>
                                updateAdvancedFilter({
                                  jobType: event.target.value,
                                })
                              }
                            >
                              <option value="">Job Type</option>
                              {userFilterOptions.jobTypeOptions.map(
                                (option) => (
                                  <option
                                    key={option.value}
                                    value={option.value}
                                  >
                                    {option.label}
                                  </option>
                                ),
                              )}
                            </select>
                            <select
                              className="form-select"
                              value={userFilters.departmentId}
                              disabled={filtersLoading}
                              onChange={(event) =>
                                updateAdvancedFilter({
                                  departmentId: event.target.value,
                                })
                              }
                            >
                              <option value="">Department</option>
                              {userFilterOptions.departmentOptions.map(
                                (option) => (
                                  <option
                                    key={option.value}
                                    value={option.value}
                                  >
                                    {option.label}
                                  </option>
                                ),
                              )}
                            </select>
                            <select
                              className="form-select"
                              value={userFilters.centerId}
                              disabled={filtersLoading}
                              onChange={(event) =>
                                updateAdvancedFilter({
                                  centerId: event.target.value,
                                })
                              }
                            >
                              <option value="">Center</option>
                              {userFilterOptions.centerOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                            <select
                              className="form-select"
                              value={userFilters.dateFilter}
                              onChange={(event) =>
                                updateAdvancedFilter(
                                  {
                                    dateFilter: event.target.value,
                                    dateRange: [],
                                  },
                                  event.target.value === "Select",
                                )
                              }
                            >
                              {dateFilterOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                            {userFilters.dateFilter !== "Select" && (
                              <DatePicker
                                className="employee-report-date-picker"
                                value={userFilters.dateRange}
                                onChange={(range) =>
                                  updateAdvancedFilter({
                                    dateRange: Array.isArray(range)
                                      ? range
                                      : [],
                                  })
                                }
                                range
                                rangeHover
                                format="YYYY/MM/DD"
                                placeholder="Date range"
                              />
                            )}
                          </>
                        )}
                      </div>
                      {hasActiveUserFilter && (
                        <button
                          type="button"
                          className="filter-clear-btn"
                          onClick={clearUserFilters}
                        >
                          Clear filters
                        </button>
                      )}
                    </div>
                    {usersLoading ? (
                      <div className="access-empty">
                        <Spinner size="sm" /> Loading users...
                      </div>
                    ) : (
                      <>
                        <div className="user-bulk-actions">
                          <Btn
                            color="light"
                            type="button"
                            className="btn-xs"
                            disabled={
                              !userOptions.length || allFilteredUsersSelected
                            }
                            onClick={selectFilteredUsers}
                          >
                            Select All
                          </Btn>
                          <span>
                            {selectedFilteredUserCount}/{userOptions.length}{" "}
                            filtered
                          </span>
                          <Btn
                            color="light"
                            type="button"
                            className="btn-xs"
                            disabled={!values.users.length}
                            onClick={clearSelectedUsers}
                          >
                            Clear Users
                          </Btn>
                        </div>
                        <div className="user-checkbox-list">
                          {orderedUserOptions.length ? (
                            orderedUserOptions.map((user) => {
                              const isSelected = selectedUserIds.has(user.id);

                              return (
                                <label
                                  className={`user-checkbox-row ${
                                    isSelected ? "is-selected" : ""
                                  }`}
                                  key={user.id}
                                >
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => toggleUser(user)}
                                  />
                                  <span className="user-checkbox-row__avatar">
                                    {user.name?.charAt(0) || "U"}
                                  </span>
                                  <span className="user-checkbox-row__copy">
                                    <span className="user-checkbox-row__name">
                                      {user.name}
                                    </span>
                                    <span className="user-checkbox-row__meta">
                                      {[
                                        user.employee_code,
                                        user.department_name,
                                        user.centre_name,
                                      ]
                                        .filter(Boolean)
                                        .join(" | ") || user.email}
                                    </span>
                                  </span>
                                  <span
                                    className={`user-checkbox-row__status ${
                                      user.status
                                        ?.toLowerCase()
                                        ?.replaceAll(" ", "") === "active"
                                        ? "is-active"
                                        : ""
                                    }`}
                                  >
                                    {user.status || "Active"}
                                  </span>
                                </label>
                              );
                            })
                          ) : (
                            <div className="access-empty access-empty--compact">
                              No users found
                            </div>
                          )}
                        </div>
                      </>
                    )}
                    {touched.users && typeof errors.users === "string" && (
                      <div className="invalid-feedback d-block">
                        {errors.users}
                      </div>
                    )}
                  </div>
                </section>
              </Col>

              <Col xl={8} lg={7}>
                <section className="access-pane access-pane--routes">
                  <div className="access-pane__header">
                    <div>
                      <P className="access-pane__title">Available Menu Access</P>
                      <P className="access-pane__subtitle">
                        Parent and child hierarchy
                      </P>
                    </div>
                    <Badge color="primary" pill>
                      {visibleMenuIds.length} routes
                    </Badge>
                  </div>

                  <div className="route-controls">
                    <div className="route-search">
                      <IconComponent className="fa fa-search" />
                      <input
                        type="search"
                        className="form-control"
                        placeholder="Search menu or route"
                        value={routeSearch}
                        onChange={(event) => setRouteSearch(event.target.value)}
                      />
                    </div>
                    <select
                      className="form-select route-filter-select"
                      value={menuRowFilter}
                      onChange={(event) =>
                        setMenuRowFilter(event.target.value as MenuRowFilter)
                      }
                    >
                      {menuRowFilterOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="permission-tree">
                    <div className="permission-tree__head">
                      <span>Available Menu Access</span>
                      <span className="permission-tree__count">
                        {visibleMenuIds.length}
                      </span>
                    </div>
                    <div className="permission-tree__body">
                      {visibleRouteGroups.length ? (
                        visibleRouteGroups.map((menu) => (
                          <MenuNode
                            key={menu.id}
                            menu={menu}
                            selectedIds={values.menuIds}
                            expandedIds={expandedIds}
                            onToggleExpand={toggleExpand}
                            onToggleMenu={toggleMenu}
                          />
                        ))
                      ) : (
                        <div className="access-empty">
                          No matching menu routes
                        </div>
                      )}
                    </div>
                  </div>
                  {touched.menuIds && typeof errors.menuIds === "string" && (
                    <div className="invalid-feedback d-block px-3">
                      {errors.menuIds}
                    </div>
                  )}
                </section>
              </Col>
            </Row>
            <div className="access-footer">
              <Btn
                color="primary"
                type="button"
                disabled={
                  accessAction !== null ||
                  !values.users.length ||
                  !values.menuIds.length
                }
                onClick={async () => {
                  const shouldClearMenus = await handleMenuAccessAction(
                    "attach",
                    values.users,
                    values.menuIds,
                  );

                  if (shouldClearMenus) setFieldValue("menuIds", []);
                }}
              >
                {accessAction === "attach" ? (
                  <Spinner size="sm" className="me-2" />
                ) : (
                  <IconComponent className="fa fa-plus me-2" />
                )}
                Add
              </Btn>
              <Btn
                color="danger"
                type="button"
                disabled={
                  accessAction !== null ||
                  !values.users.length ||
                  !values.menuIds.length
                }
                onClick={async () => {
                  const shouldClearMenus = await handleMenuAccessAction(
                    "detach",
                    values.users,
                    values.menuIds,
                  );

                  if (shouldClearMenus) setFieldValue("menuIds", []);
                }}
              >
                {accessAction === "detach" ? (
                  <Spinner size="sm" className="me-2" />
                ) : (
                  <IconComponent className="fa fa-minus me-2" />
                )}
                Remove
              </Btn>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

export default UserMenuForm;
